/**
 * ============================================================================
 * MENTAL LOAD PICKER v2.0
 * ============================================================================
 *
 * Modal zur Auswahl von Mental Load Aufgaben für Notizen.
 * Basiert auf ProductPicker Design Pattern.
 *
 * Features:
 * - Kategorien mit einklappbaren Aufgabenlisten
 * - Suchfunktion
 * - Häufig verwendete Aufgaben
 * - Planivio Compact Design
 * - ✅ v2.0: Überträgt auch description + subtasks
 * - ✅ v2.1: SINGLE SELECT - Klick übernimmt direkt
 *
 * @version 2.1.0
 */

import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import {
  MENTAL_LOAD_CATALOG,
  ALL_MENTAL_LOAD_TASKS,
  searchMentalLoadTasks,
  getFrequentMentalLoadTasks
} from './mentalLoadCatalog';
import './MentalLoadPicker.css';

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

// Suchleiste
const TaskSearch = ({ value, onChange, onClear }) => {
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  return (
    <div className="ml-search-container">
      <span className="ml-search-icon">🔍</span>
      <input
        ref={inputRef}
        type="text"
        placeholder="Aufgabe suchen..."
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="ml-search-input"
      />
      {value && (
        <button onClick={onClear} className="ml-search-clear">
          ✕
        </button>
      )}
    </div>
  );
};

// ✅ v2.1: Einzelne Aufgabe - SINGLE SELECT (direkt übernehmen)
const TaskItem = ({ task, onSelect }) => {
  return (
    <div
      className="ml-task-item"
      onClick={() => onSelect(task)}
    >
      <span className="ml-task-emoji">{task.emoji}</span>
      <span className="ml-task-name">{task.name}</span>
      <button className="ml-task-add">→</button>
    </div>
  );
};

// ✅ v2.1: Einklappbare Kategorie - SINGLE SELECT
const TaskCategory = ({
  category,
  isExpanded,
  onToggleExpand,
  onSelectTask
}) => {
  return (
    <div className="ml-category">
      <div
        className={`ml-category-header ${isExpanded ? 'expanded' : ''}`}
        onClick={onToggleExpand}
      >
        <span className="ml-category-emoji">{category.emoji}</span>
        <span className="ml-category-label">{category.label}</span>
        <span className="ml-category-badge">{category.tasks.length}</span>
        <span className="ml-category-chevron">{isExpanded ? '▼' : '▶'}</span>
      </div>

      {isExpanded && (
        <div className="ml-category-tasks">
          {category.tasks.map(task => (
            <TaskItem
              key={task.id}
              task={task}
              onSelect={onSelectTask}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ✅ v2.1: SelectedTasksPreview entfernt - Single Select braucht keine Vorschau

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const MentalLoadPicker = ({ isOpen, onClose, onAddTasks }) => {
  // State - ✅ v2.1: Kein selectedTasks mehr (Single Select)
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCategories, setExpandedCategories] = useState(['haushalt', 'kinder']);

  // Memoized Data
  const frequentTasks = useMemo(() => getFrequentMentalLoadTasks(), []);

  const searchResults = useMemo(() => {
    if (searchQuery.length < 2) return [];
    return searchMentalLoadTasks(searchQuery);
  }, [searchQuery]);

  const isSearching = searchQuery.length >= 2;

  // Handlers
  const toggleCategory = useCallback((categoryId) => {
    setExpandedCategories(prev =>
      prev.includes(categoryId)
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  }, []);

  // ✅ v2.1: SINGLE SELECT - Direkt übernehmen bei Klick
  const selectTask = useCallback((task) => {
    // Alle Task-Daten inkl. description und subtasks übertragen
    const selectedTask = {
      id: task.id,
      name: task.name,
      emoji: task.emoji,
      categoryLabel: task.categoryLabel || getCategoryLabel(task.id),
      description: task.description || '',
      subtasks: task.subtasks || []
    };

    console.log('🧠 Task selected:', selectedTask.name, 'with', selectedTask.subtasks?.length || 0, 'subtasks');

    // Direkt an Parent übergeben und schließen
    onAddTasks([selectedTask]);
    setSearchQuery('');
  }, [onAddTasks]);

  // ✅ v2.1: removeFromSelection, clearSelection, handleAddToNotes entfernt (Single Select)

  const handleClose = () => {
    setSearchQuery('');
    onClose();
  };

  // Helper: Get category label for a task
  const getCategoryLabel = (taskId) => {
    for (const cat of Object.values(MENTAL_LOAD_CATALOG)) {
      if (cat.tasks.some(t => t.id === taskId)) {
        return cat.label;
      }
    }
    return 'Sonstiges';
  };

  // Don't render if not open
  if (!isOpen) return null;

  return (
    <div className="ml-picker-overlay" onClick={handleClose}>
      <div className="ml-picker-modal" onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div className="ml-picker-header">
          <h2 className="ml-picker-title">
            <span>🧠</span> Mental Load Aufgaben
          </h2>
          <button className="ml-picker-close" onClick={handleClose}>✕</button>
        </div>

        {/* Search */}
        <div className="ml-picker-search">
          <TaskSearch
            value={searchQuery}
            onChange={setSearchQuery}
            onClear={() => setSearchQuery('')}
          />
        </div>

        {/* Content - ✅ v2.1: Single Select */}
        <div className="ml-picker-content">

          {isSearching ? (
            // Search Results
            <div className="ml-search-results">
              <div className="ml-section-header">
                🔍 Suchergebnisse ({searchResults.length})
              </div>
              {searchResults.length === 0 ? (
                <div className="ml-no-results">
                  <p>Keine Aufgaben gefunden für "{searchQuery}"</p>
                </div>
              ) : (
                <div className="ml-results-list">
                  {searchResults.map(task => (
                    <TaskItem
                      key={task.id}
                      task={task}
                      onSelect={selectTask}
                    />
                  ))}
                </div>
              )}
            </div>
          ) : (
            // Category Browsing
            <>
              {/* Frequent Tasks - Klick übernimmt direkt */}
              <div className="ml-frequent-section">
                <div className="ml-section-header">
                  ⭐ Häufige Aufgaben
                </div>
                <div className="ml-frequent-chips">
                  {frequentTasks.map(task => (
                    <button
                      key={task.id}
                      className="ml-frequent-chip"
                      onClick={() => selectTask(task)}
                    >
                      <span>{task.emoji}</span>
                      <span>{task.name}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Categories */}
              <div className="ml-categories-section">
                {Object.values(MENTAL_LOAD_CATALOG).map(category => (
                  <TaskCategory
                    key={category.id}
                    category={category}
                    isExpanded={expandedCategories.includes(category.id)}
                    onToggleExpand={() => toggleCategory(category.id)}
                    onSelectTask={selectTask}
                  />
                ))}
              </div>
            </>
          )}
        </div>

        {/* ✅ v2.1: Footer nur mit Abbrechen-Button */}
        <div className="ml-picker-footer">
          <button className="ml-btn-cancel" onClick={handleClose} style={{ flex: 1 }}>
            Abbrechen
          </button>
        </div>
      </div>
    </div>
  );
};

export default MentalLoadPicker;
